import { AuthCallbackHandler } from "@/components/auth/auth-callback-handler";
import { AuthLoading } from "@/components/auth/auth-loading";
import { AuthRedirect } from "@/components/auth/auth-redirect";
import {
  OwnerProtectedRoute,
  ProtectedRoute,
  RequirePermission,
} from "@/components/auth/protected-routes";
import { StudioPermission } from "@dnceclub/shared";
import { AdminProtectedRoute } from "@/components/auth/admin-protected-route";
import { Layout } from "@/components/layout/layout";
import { OwnerLayoutWrapper } from "@/components/owner/owner-layout-wrapper";
import { DemoOwnerLayout } from "@/components/demo/demo-owner-layout";
import { DeferredAnalyticsProvider } from "@/components/providers/deferred-analytics-provider";
import { GoogleMapsProvider } from "@/components/providers/google-maps-provider";
import { ScrollToTop } from "@/components/scroll-to-top";
import { SentryErrorBoundary } from "@/components/sentry-error-boundary";
import { AuthProvider, useAuth } from "@/lib/contexts/auth-context";
import { startAuthMonitoring } from "@/lib/auth-monitor";
import { lazyWithRetry, clearChunkReloadMarker } from "@/lib/chunk-error-handler";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

// Eagerly load the home page since it's the most common entry point
import { HomePage } from "@/pages/home";
import { ProfileClassVideosPage } from "@/pages/profile-class-videos";

// Lazy load all other pages with retry logic
const SignInPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/signin").then((m) => ({ default: m.SignInPage })))
);
const SignUpPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/signup").then((m) => ({ default: m.SignUpPage })))
);
const ResetPasswordPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/reset-password").then((m) => ({
    default: m.ResetPasswordPage,
  })))
);
const UpdatePasswordPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/update-password").then((m) => ({
    default: m.UpdatePasswordPage,
  })))
);
const EmailVerificationRequiredPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/email-verification-required").then((m) => ({
    default: m.EmailVerificationRequiredPage,
  })))
);
const ConfirmEmailSuccessPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/confirm-email-success").then((m) => ({
    default: m.ConfirmEmailSuccessPage,
  })))
);
const AuthCallbackPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/auth/callback").then((m) => ({
    default: m.AuthCallbackPage,
  })))
);
const StudiosPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/studios").then((m) => ({ default: m.StudiosPage })))
);
const SearchResultsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/studios/search").then((m) => ({ default: m.SearchResultsPage })))
);
const BrowseClassesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/classes").then((m) => ({ default: m.BrowseClassesPage })))
);
const FavoritedInstructorsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/favorites/instructors").then((m) => ({ default: m.FavoritedInstructorsPage })))
);
const EmailConfirmationPage = lazy(
  () => lazyWithRetry(() => import("@/pages/auth/email-confirmation"))
);
const OwnerDashboard = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/dashboard").then((m) => ({ default: m.OwnerDashboard })))
);
const SettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/settings").then((m) => ({ default: m.SettingsPage })))
);
const RoomsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/rooms").then((m) => ({ default: m.RoomsPage })))
);
const RoomFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/room-form").then((m) => ({ default: m.RoomFormPage })))
);
const BookingsManagementPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/bookings").then((m) => ({
    default: m.BookingsManagementPage,
  })))
);
const BookingDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/booking-detail").then((m) => ({
    default: m.BookingDetailPage,
  })))
);
const ModifyBookingPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/modify-booking").then((m) => ({
    default: m.ModifyBookingPage,
  })))
);
const CreateManualBookingPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/create-manual-booking").then((m) => ({
    default: m.CreateManualBookingPage,
  })))
);
const HelpPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/help").then((m) => ({
    default: m.HelpPage,
  })))
);
const RenterDashboardPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/dashboard").then((m) => ({ default: m.RenterDashboardPage })))
);
const BookRoomPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/book-room").then((m) => ({ default: m.BookRoomPage })))
);
const BookingConfirmationPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/booking-confirmation-with-payment").then((m) => ({
    default: m.BookingConfirmationWithPaymentPage,
  })))
);
const RenterBookingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/bookings").then((m) => ({
    default: m.RenterBookingsPage,
  })))
);
const AllBookingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/all-bookings").then((m) => ({
    default: m.AllBookingsPage,
  })))
);
const RenterBookingDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/booking-detail").then((m) => ({
    default: m.RenterBookingDetailPage,
  })))
);
const ProfilePage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/profile").then((m) => ({ default: m.ProfilePage })))
);
const ProfileAnalyticsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/profile-analytics").then((m) => ({ default: m.ProfileAnalyticsPage })))
);
const AddClassPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/add-class").then((m) => ({ default: m.AddClassPage })))
);
const PublicClassDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/class-detail").then((m) => ({ default: m.ClassDetailPage })))
);
const PublicSeriesDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/series-detail").then((m) => ({ default: m.SeriesDetailPage })))
);
const AddSeriesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/add-series").then((m) => ({ default: m.AddSeriesPage })))
);
const EditClassPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/edit-class").then((m) => ({ default: m.EditClassPage })))
);
const EditSeriesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/edit-series").then((m) => ({ default: m.EditSeriesPage })))
);
const ClassRegistrationsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/class-registrations").then((m) => ({ default: m.ClassRegistrationsPage })))
);
const SeriesRegistrationsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/series-registrations").then((m) => ({ default: m.SeriesRegistrationsPage })))
);
const ClassSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/class-settings").then((m) => ({ default: m.ClassSettingsPage })))
);
const BookingSuccessPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/booking-success").then((m) => ({
    default: m.BookingSuccessPage,
  })))
);
const RenterRewardsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/rewards").then((m) => ({
    default: m.RenterRewardsPage,
  })))
);
const RoomDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/room-detail").then((m) => ({ default: m.RoomDetailPage })))
);
const StudioDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/studio-detail").then((m) => ({ default: m.StudioDetailPage })))
);
const FeesGuide = lazy(() =>
  lazyWithRetry(() => import("@/pages/fees-guide").then((m) => ({ default: m.FeesGuide })))
);
const OwnerSignupPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner-signup").then((m) => ({ default: m.OwnerSignupPage })))
);
const HowItWorksPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/how-it-works").then((m) => ({ default: m.HowItWorksPage })))
);
const FaqPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/faq").then((m) => ({ default: m.FaqPage })))
);
const ContactPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/contact").then((m) => ({ default: m.ContactPage })))
);
const PrivacyPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/privacy").then((m) => ({ default: m.PrivacyPage })))
);
const TermsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/terms").then((m) => ({ default: m.TermsPage })))
);
const RoomAvailability = lazy(() => lazyWithRetry(() => import("@/pages/owner/room-availability")));
const PayoutsPage = lazy(() => lazyWithRetry(() => import("@/pages/owner/payouts")));
const PayoutDetailPage = lazy(() => lazyWithRetry(() => import("@/pages/owner/payout-detail")));
const InstructorPayoutDetailPage = lazy(() => lazyWithRetry(() => import("@/pages/owner/instructor-payout-detail")));
const PayoutSettingsPage = lazy(() => lazyWithRetry(() => import("@/pages/owner/payout-settings")));
const CreateStudioPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/create-studio").then((m) => ({
    default: m.CreateStudioPage,
  })))
);
const SetupPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/setup").then((m) => ({ default: m.SetupPage })))
);
const AnalyticsDashboard = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/analytics").then((m) => ({
    default: m.AnalyticsDashboard,
  })))
);
const DiscountsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/discounts").then((m) => ({
    default: m.DiscountsPage,
  })))
);
const DiscountFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/discount-form").then((m) => ({
    default: m.DiscountFormPage,
  })))
);
const DiscountDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/discount-detail").then((m) => ({
    default: m.DiscountDetailPage,
  })))
);
const RewardsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/rewards").then((m) => ({
    default: m.RewardsPage,
  })))
);
const RewardFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/reward-form").then((m) => ({
    default: m.RewardFormPage,
  })))
);
const RewardDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/reward-detail").then((m) => ({
    default: m.RewardDetailPage,
  })))
);
const ClassesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/classes").then((m) => ({
    default: m.ClassesPage,
  })))
);
const ClassFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-form").then((m) => ({
    default: m.ClassFormPage,
  })))
);
const ClassDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-detail").then((m) => ({
    default: m.ClassDetailPage,
  })))
);
const ClassTypesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-types").then((m) => ({
    default: m.ClassTypesPage,
  })))
);
const ClassTypeFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-type-form").then((m) => ({
    default: m.ClassTypeFormPage,
  })))
);
const EventsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/events").then((m) => ({
    default: m.EventsPage,
  })))
);
const OwnerEventDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/event-detail").then((m) => ({
    default: m.OwnerEventDetailPage,
  })))
);
const OwnerEventFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/event-form").then((m) => ({
    default: m.OwnerEventFormPage,
  })))
);
const InstructorsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/instructors").then((m) => ({
    default: m.InstructorsPage,
  })))
);
const InstructorDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/instructor").then((m) => ({
    default: m.InstructorDetailPage,
  })))
);
const CustomersPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/customers").then((m) => ({
    default: m.CustomersPage,
  })))
);
const CustomerDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/customer-detail").then((m) => ({
    default: m.CustomerDetailPage,
  })))
);
const CustomersPike13Page = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/customers-pike13").then((m) => ({
    default: m.CustomersPike13Page,
  })))
);
const CustomersPike13DetailPage = lazy(() =>
  lazyWithRetry(() =>
    import("@/pages/owner/customers-pike13-detail").then((m) => ({
      default: m.CustomersPike13DetailPage,
    }))
  )
);
const ClassPacksPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-packs").then((m) => ({
    default: m.ClassPacksPage,
  })))
);
const ClassPackDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-pack-detail").then((m) => ({
    default: m.ClassPackDetailPage,
  })))
);
const MembershipsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/memberships").then((m) => ({
    default: m.MembershipsPage,
  })))
);
const MembershipDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/membership-detail").then((m) => ({
    default: m.MembershipDetailPage,
  })))
);
const MembershipFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/membership-form").then((m) => ({
    default: m.MembershipFormPage,
  })))
);
const InvoicesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/invoices").then((m) => ({ default: m.InvoicesPage })))
);
const CreateInvoicePage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/create-invoice").then((m) => ({ default: m.CreateInvoicePage })))
);
const StandaloneInvoiceDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/invoice-detail").then((m) => ({ default: m.InvoiceDetailPage })))
);
const InvoiceSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/invoice-settings").then((m) => ({
    default: m.InvoiceSettingsPage,
  })))
);
const GiftCardsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/gift-cards").then((m) => ({
    default: m.GiftCardsPage,
  })))
);
const StaffManagementPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/staff-management").then((m) => ({
    default: m.StaffManagementPage,
  })))
);
const RolePermissionsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/role-permissions").then((m) => ({
    default: m.RolePermissionsPage,
  })))
);
const StaffInviteAcceptPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/staff-invite").then((m) => ({
    default: m.StaffInviteAcceptPage,
  })))
);
const MyMembershipsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/my-memberships"))
);
const ProPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/pro").then((m) => ({ default: m.ProPage })))
);

// Admin pages
const AdminDashboard = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/dashboard").then((m) => ({
    default: m.AdminDashboard,
  })))
);
const AdminShowcase = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/showcase").then((m) => ({
    default: m.AdminShowcase,
  })))
);
const AdminCarouselApril2026 = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/carousel-april-2026").then((m) => ({
    default: m.AdminCarouselApril2026,
  })))
);
const AdminCarouselInstructorAnalytics = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/carousel-instructor-analytics").then((m) => ({
    default: m.AdminCarouselInstructorAnalytics,
  })))
);
const AdminCarouselQrCheckin = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/carousel-qr-checkin").then((m) => ({
    default: m.AdminCarouselQrCheckin,
  })))
);
const AdminCarouselEvents = lazy(() =>
  lazyWithRetry(() => import("@/pages/admin/carousel-events").then((m) => ({
    default: m.AdminCarouselEvents,
  })))
);
// Demo pages
const DemoOwnerDashboard = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/owner-dashboard").then((m) => ({
    default: m.DemoOwnerDashboard,
  })))
);
const DemoPayoutsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/payouts").then((m) => ({
    default: m.DemoPayoutsPage,
  })))
);
const DemoSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/settings").then((m) => ({
    default: m.DemoSettingsPage,
  })))
);
const DemoBasicSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/settings/basic").then((m) => ({
    default: m.BasicSettingsPage,
  })))
);
const DemoPoliciesSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/settings/policies").then((m) => ({
    default: m.DemoPoliciesSettingsPage,
  })))
);
const DemoBookingSettingsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/settings/booking").then((m) => ({
    default: m.DemoBookingSettingsPage,
  })))
);
const DemoRoomAvailability = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/room-availability"))
);
// const DemoRoomEditPage = lazy(() =>
//   lazyWithRetry(() => import("@/pages/demo/room-edit").then((m) => ({ default: m.DemoRoomEditPage })))
// );
const DemoBookingsManagementPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/bookings").then((m) => ({
    default: m.DemoBookingsManagementPage,
  })))
);
const DemoBookingDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/booking-detail").then((m) => ({
    default: m.DemoBookingDetailPage,
  })))
);
const DemoRoomEditPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/demo/room-edit").then((m) => ({
    default: m.default,
  })))
);
const InvoicePage = lazy(() =>
  lazyWithRetry(() => import("@/pages/invoice").then((m) => ({
    default: m.InvoicePage,
  })))
);
const StandaloneInvoicePage = lazy(() =>
  lazyWithRetry(() => import("@/pages/standalone-invoice").then((m) => ({
    default: m.StandaloneInvoicePage,
  })))
);
const StudioHandlePage = lazy(() =>
  lazyWithRetry(() => import("@/pages/studio-handle-page").then((m) => ({
    default: m.StudioHandlePage,
  })))
);
const ClassVideosPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/owner/class-videos").then((m) => ({
    default: m.ClassVideosPage,
  })))
);
const AddEventPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/add-event").then((m) => ({ default: m.AddEventPage })))
);
const EditEventPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/edit-event").then((m) => ({ default: m.EditEventPage })))
);
const EventDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/event-detail").then((m) => ({ default: m.EventDetailPage })))
);
const EventPurchasesPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/renter/event-purchases").then((m) => ({ default: m.EventPurchasesPage })))
);
const InstructorProgramsPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/instructor/programs").then((m) => ({
    default: m.InstructorProgramsPage,
  })))
);
const ProgramFormPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/instructor/program-form-page").then((m) => ({
    default: m.ProgramFormPage,
  })))
);
const InstructorProgramDetailPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/instructor/program-detail").then((m) => ({
    default: m.InstructorProgramDetailPage,
  })))
);
const ProgramListingPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/program/program-listing").then((m) => ({
    default: m.ProgramListingPage,
  })))
);
const ProgramApplyPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/program/program-apply").then((m) => ({
    default: m.ProgramApplyPage,
  })))
);
const ProgramCheckoutPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/program/program-checkout").then((m) => ({
    default: m.ProgramCheckoutPage,
  })))
);
const MyEnrollmentPage = lazy(() =>
  lazyWithRetry(() => import("@/pages/program/my-enrollment").then((m) => ({
    default: m.MyEnrollmentPage,
  })))
);

// Redirect old /invoice/s/:id URLs to new /invoice/:id
function InvoiceRedirect() {
  const { id } = useParams();
  return <Navigate to={`/invoice/${id}`} replace />;
}

function App() {
  useEffect(() => {
    // App mounted successfully — clear the reload marker so a later chunk error
    // (e.g. a transient network failure on navigation) doesn't get treated as
    // a reload loop and show "Update required".
    clearChunkReloadMarker();

    // Start auth monitoring in development
    if (import.meta.env.DEV) {
      const stopMonitoring = startAuthMonitoring();
      return stopMonitoring;
    }
  }, []);

  return (
    <SentryErrorBoundary showDialog>
      <DeferredAnalyticsProvider>
        <BrowserRouter>
          <AuthProvider>
            <AuthCallbackHandler>
              <AppContent />
            </AuthCallbackHandler>
          </AuthProvider>
        </BrowserRouter>
      </DeferredAnalyticsProvider>
    </SentryErrorBoundary>
  );
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  return (
    <GoogleMapsProvider>
      <AuthRedirect>
        {/* Fixed background div for mobile compatibility */}
        <div
          id="background"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: -1,
            backgroundImage: 'url(https://zkpoydaapxzjkeljxioz.supabase.co/storage/v1/object/public/gradients/gradient_8.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none'
          }}
          className="md:!bg-[url('https://zkpoydaapxzjkeljxioz.supabase.co/storage/v1/object/public/gradients/gradient_8_desktop.webp')] xl:!bg-[url('https://zkpoydaapxzjkeljxioz.supabase.co/storage/v1/object/public/gradients/gradient_8_4k.webp')]"
        />
        <ScrollToTop />
        <Toaster position="top-center" />
        <Suspense fallback={<AuthLoading />}>
          <div className="animate-in fade-in duration-300">
            <Routes>
              {/* Public routes with main layout */}
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="signin" element={<SignInPage />} />
                <Route path="signup" element={<SignUpPage />} />
                <Route
                  path="auth/reset-password"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="auth/update-password"
                  element={<UpdatePasswordPage />}
                />
                <Route
                  path="auth/confirm-email"
                  element={<EmailConfirmationPage />}
                />
                <Route
                  path="auth/confirm-email-success"
                  element={<ConfirmEmailSuccessPage />}
                />
                <Route
                  path="auth/email-verification-required"
                  element={<EmailVerificationRequiredPage />}
                />
                <Route path="auth/callback" element={<AuthCallbackPage />} />
                <Route path="studios" element={<StudiosPage />} />
                <Route path="studios/search" element={<SearchResultsPage />} />
                <Route path="classes" element={<BrowseClassesPage />} />
                <Route path="classes/:classId" element={<PublicClassDetailPage />} />
                <Route path="classes/:classId/videos" element={<ClassVideosPage />} />
                <Route path="series/:seriesId" element={<PublicSeriesDetailPage />} />
                <Route
                  path="favorite-instructors"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <FavoritedInstructorsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="studios/:studioId"
                  element={<StudioDetailPage />}
                />
                <Route path="rooms/:roomId" element={<RoomDetailPage />} />
                <Route path="fees" element={<FeesGuide />} />
                <Route path="join" element={<OwnerSignupPage />} />
                <Route path="how-it-works" element={<HowItWorksPage />} />
                <Route path="faq" element={<FaqPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="invoice/:id" element={<StandaloneInvoicePage />} />
                <Route path="booking/invoice/:token" element={<InvoicePage />} />
                {/* Redirect old standalone invoice URL */}
                <Route path="invoice/s/:id" element={<InvoiceRedirect />} />

                {/* User Routes - Non-owners can book studios */}
                <Route
                  path="rooms/:roomId/book"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <BookRoomPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="booking-confirmation"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <BookingConfirmationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="dashboard"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <RenterDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rentals"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <RenterBookingsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Keep old /bookings route for backwards compatibility */}
                <Route
                  path="bookings"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <RenterBookingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="bookings/all"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <AllBookingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="bookings/:bookingId"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <RenterBookingDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile/analytics"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProfileAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="classes/new"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <AddClassPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="series/new"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <AddSeriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="classes/:classId/edit"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <EditClassPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="series/:seriesId/edit"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <EditSeriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="classes/:classId/registrations"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ClassRegistrationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="series/:seriesId/registrations"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <SeriesRegistrationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="class-settings"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ClassSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="bookings/:bookingId/success"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <BookingSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rewards"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <RenterRewardsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="memberships"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <MyMembershipsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="pro"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="events/new"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <AddEventPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="events/:eventId/edit"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <EditEventPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="events/:eventId" element={<EventDetailPage />} />
                <Route
                  path="programs/new"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProgramFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="programs/:programId/edit"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProgramFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="programs"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <InstructorProgramsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="programs/:programId/manage"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <InstructorProgramDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="events/:eventId/purchases"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <EventPurchasesPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="program/:id" element={<ProgramListingPage />} />
                <Route
                  path="program/:id/apply"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProgramApplyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="program/:id/checkout"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <ProgramCheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="program/:id/my-enrollment"
                  element={
                    <ProtectedRoute requireOwner={false}>
                      <MyEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route path=":handle/videos" element={<ProfileClassVideosWrapper />} />

                {/* Carousel pages (no auth) */}
                <Route path="carousel-april-2026" element={<AdminCarouselApril2026 />} />
                <Route path="carousel-instructor-analytics" element={<AdminCarouselInstructorAnalytics />} />
                <Route path="carousel-qr-checkin" element={<AdminCarouselQrCheckin />} />
                <Route path="carousel-events" element={<AdminCarouselEvents />} />

                {/* Catch-all route for studio handles - must be last within Layout */}
                <Route path=":handle" element={<StudioHandlePage />} />
              </Route>

              {/* Staff Invite Accept - standalone page outside Layout */}
              <Route
                path="staff-invite/:token"
                element={<StaffInviteAcceptPage />}
              />

              {/* Owner create-studio - standalone page outside Layout */}
              <Route
                path="owner/create-studio"
                element={
                  <OwnerProtectedRoute skipOnboarding>
                    <CreateStudioPage />
                  </OwnerProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="admin"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="showcase"
                element={<AdminShowcase />}
              />
              {/* Demo Owner Routes - No authentication required */}
              <Route path="demo/owner" element={<DemoOwnerLayout />}>
                <Route index element={<DemoOwnerDashboard />} />
                <Route path="dashboard" element={<DemoOwnerDashboard />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="bookings" element={<DemoBookingsManagementPage />} />
                <Route path="bookings/:bookingId" element={<DemoBookingDetailPage />} />
                <Route path="rooms/:roomId/availability" element={<DemoRoomAvailability />} />
                <Route path="rooms/:roomId/edit" element={<DemoRoomEditPage />} />
                <Route path="payouts" element={<DemoPayoutsPage />} />
                <Route path="settings" element={<DemoSettingsPage />} />
                <Route path="settings/basic" element={<DemoBasicSettingsPage />} />
                <Route path="settings/policies" element={<DemoPoliciesSettingsPage />} />
                <Route path="settings/booking" element={<DemoBookingSettingsPage />} />
              </Route>

              {/* Owner Routes with sidebar layout */}
              <Route
                path="owner"
                element={
                  <OwnerProtectedRoute>
                    <OwnerLayoutWrapper />
                  </OwnerProtectedRoute>
                }
              >
                <Route path="dashboard" element={<OwnerDashboard />} />
                <Route path="setup" element={<SetupPage />} />
                <Route
                  path="analytics"
                  element={
                    <RequirePermission permission={StudioPermission.ANALYTICS_VIEW}>
                      <AnalyticsDashboard />
                    </RequirePermission>
                  }
                />
                <Route path="rooms" element={<RoomsPage />} />
                <Route path="rooms/new" element={<RoomFormPage />} />
                <Route path="rooms/:roomId/edit" element={<RoomFormPage />} />
                <Route
                  path="rooms/:roomId/availability"
                  element={<RoomAvailability />}
                />
                <Route
                  path="bookings"
                  element={
                    <RequirePermission permission={StudioPermission.BOOKINGS_VIEW}>
                      <BookingsManagementPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="bookings/create-manual"
                  element={
                    <RequirePermission permission={StudioPermission.BOOKINGS_VIEW}>
                      <CreateManualBookingPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="bookings/:bookingId"
                  element={
                    <RequirePermission permission={StudioPermission.BOOKINGS_VIEW}>
                      <BookingDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="bookings/:bookingId/modify"
                  element={
                    <RequirePermission permission={StudioPermission.BOOKINGS_VIEW}>
                      <ModifyBookingPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="invoices"
                  element={
                    <RequirePermission permission={StudioPermission.INVOICES_VIEW}>
                      <InvoicesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="invoices/create"
                  element={
                    <RequirePermission permission={StudioPermission.INVOICES_CREATE}>
                      <CreateInvoicePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="invoices/:invoiceId"
                  element={
                    <RequirePermission permission={StudioPermission.INVOICES_VIEW}>
                      <StandaloneInvoiceDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="invoices/:invoiceId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.INVOICES_VIEW}>
                      <CreateInvoicePage />
                    </RequirePermission>
                  }
                />
                <Route path="invoices/settings" element={<InvoiceSettingsPage />} />
                <Route
                  path="payouts"
                  element={
                    <RequirePermission permission={StudioPermission.PAYOUTS_VIEW}>
                      <PayoutsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="payouts/instructor/:date"
                  element={
                    <RequirePermission permission={StudioPermission.PAYOUTS_VIEW}>
                      <InstructorPayoutDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="payouts/:payoutId"
                  element={
                    <RequirePermission permission={StudioPermission.PAYOUTS_VIEW}>
                      <PayoutDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="payout-settings"
                  element={
                    <RequirePermission permission={StudioPermission.PAYOUTS_VIEW}>
                      <PayoutSettingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <RequirePermission permission={StudioPermission.SETTINGS_VIEW}>
                      <SettingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="discounts"
                  element={
                    <RequirePermission permission={StudioPermission.DISCOUNTS_VIEW}>
                      <DiscountsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="discounts/new"
                  element={
                    <RequirePermission permission={StudioPermission.DISCOUNTS_CREATE}>
                      <DiscountFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="discounts/:discountId"
                  element={
                    <RequirePermission permission={StudioPermission.DISCOUNTS_VIEW}>
                      <DiscountDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="discounts/:discountId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.DISCOUNTS_EDIT}>
                      <DiscountFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="rewards"
                  element={
                    <RequirePermission permission={StudioPermission.REWARDS_VIEW}>
                      <RewardsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="rewards/new"
                  element={
                    <RequirePermission permission={StudioPermission.REWARDS_CREATE}>
                      <RewardFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="rewards/:rewardId"
                  element={
                    <RequirePermission permission={StudioPermission.REWARDS_VIEW}>
                      <RewardDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="rewards/:rewardId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.REWARDS_EDIT}>
                      <RewardFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="classes"
                  element={
                    <RequirePermission permission={StudioPermission.CLASSES_VIEW}>
                      <ClassesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="classes/new"
                  element={
                    <RequirePermission permission={StudioPermission.CLASSES_MODIFY}>
                      <ClassFormPage />
                    </RequirePermission>
                  }
                />
                <Route path="classes/types" element={<ClassTypesPage />} />
                <Route path="classes/types/new" element={<ClassTypeFormPage />} />
                <Route path="classes/types/:classTypeId/edit" element={<ClassTypeFormPage />} />
                <Route
                  path="classes/:classId"
                  element={
                    <RequirePermission permission={StudioPermission.CLASSES_VIEW}>
                      <ClassDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="classes/:classId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.CLASSES_MODIFY}>
                      <ClassFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="classes/:classId/videos"
                  element={
                    <RequirePermission permission={StudioPermission.CLASSES_VIEW}>
                      <ClassVideosPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="events"
                  element={
                    <RequirePermission permission={StudioPermission.EVENTS_VIEW}>
                      <EventsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="events/new"
                  element={
                    <RequirePermission permission={StudioPermission.EVENTS_MODIFY}>
                      <OwnerEventFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="events/:eventId"
                  element={
                    <RequirePermission permission={StudioPermission.EVENTS_VIEW}>
                      <OwnerEventDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="events/:eventId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.EVENTS_MODIFY}>
                      <OwnerEventFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="class-packs"
                  element={
                    <RequirePermission permission={StudioPermission.CLASS_PACKS_VIEW}>
                      <ClassPacksPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="class-packs/:packId"
                  element={
                    <RequirePermission permission={StudioPermission.CLASS_PACKS_VIEW}>
                      <ClassPackDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="memberships"
                  element={
                    <RequirePermission permission={StudioPermission.MEMBERSHIPS_VIEW}>
                      <MembershipsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="memberships/new"
                  element={
                    <RequirePermission permission={StudioPermission.MEMBERSHIPS_CREATE}>
                      <MembershipFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="memberships/:tierId"
                  element={
                    <RequirePermission permission={StudioPermission.MEMBERSHIPS_VIEW}>
                      <MembershipDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="memberships/:tierId/edit"
                  element={
                    <RequirePermission permission={StudioPermission.MEMBERSHIPS_MODIFY}>
                      <MembershipFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="instructors"
                  element={
                    <RequirePermission permission={StudioPermission.INSTRUCTORS_VIEW}>
                      <InstructorsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="instructors/:instructorId"
                  element={
                    <RequirePermission permission={StudioPermission.INSTRUCTORS_VIEW}>
                      <InstructorDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="customers"
                  element={
                    <RequirePermission permission={StudioPermission.CUSTOMERS_VIEW}>
                      <CustomersPage />
                    </RequirePermission>
                  }
                />
                {/* Pike13 detail must come BEFORE the dynamic `customers/:customerId`
                    so the static `pike13` segment isn't captured as :customerId. */}
                <Route
                  path="customers/pike13/:id"
                  element={
                    <RequirePermission permission={StudioPermission.CUSTOMERS_VIEW}>
                      <CustomersPike13DetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="customers/pike13"
                  element={
                    <RequirePermission permission={StudioPermission.CUSTOMERS_VIEW}>
                      <CustomersPike13Page />
                    </RequirePermission>
                  }
                />
                <Route
                  path="customers/:customerId"
                  element={
                    <RequirePermission permission={StudioPermission.CUSTOMERS_VIEW}>
                      <CustomerDetailPage />
                    </RequirePermission>
                  }
                />
                <Route path="gift-cards" element={<GiftCardsPage />} />
                <Route path="staff" element={<StaffManagementPage />} />
                <Route path="staff/permissions" element={<RolePermissionsPage />} />
                <Route path="help" element={<HelpPage />} />
              </Route>
            </Routes>
          </div>
        </Suspense>
      </AuthRedirect>
    </GoogleMapsProvider>
  );
}

function ProfileClassVideosWrapper() {
  const { handle } = useParams<{ handle: string }>();
  if (!handle) return null;
  const cleanHandle = handle.startsWith("@") ? handle.slice(1) : handle;
  return <ProfileClassVideosPage username={cleanHandle} />;
}

export default App;

